import { useEffect, useCallback, useState } from "react";
import { Dropdown } from "../../components/dropdown";
import { FeatureStatusList } from "../../constants/featureStatusList";
import "../../assets/css/setttings.css";
import { FeaturesController } from "../../controllers/featuresController";
import { useToastPromise } from "../../components/toastPromise";
import Button from "@mui/material/Button";
import { FeaturesList } from "../../constants/featuresList";
import { Textbox } from "../../components/textbox";
import { Checkbox } from "../../components/checkbox";

export const AllFeatures = ({ user }) => {
  const toastPromise = useToastPromise();
  const [loading, setLoading] = useState(false);
  const [featuresList, setFeaturesList] = useState([]);
  const [featureStatuses, setFeatureStatuses] = useState({});
  const [originalFeatureStatuses, setOriginalFeatureStatuses] = useState({});
  const [showSave, setShowSave] = useState({});
  const [sequenceChanged, setSequenceChanged] = useState(false);
  const [draggedFeature, setDraggedFeature] = useState(null);
  const [hrefs, setHrefs] = useState({});
  const [isNavBars, setIsNavBars] = useState({});
  const [isShowSectionTitles, setIsShowSectionTitles] = useState({});
  const onHandleChange = useCallback(
    (value, name) => {
      setFeatureStatuses((prevStatuses) => ({
        ...prevStatuses,
        [name]: value,
      }));
      setShowSave((prevShowSave) => ({
        ...prevShowSave,
        [name]: value !== originalFeatureStatuses[name],
      }));
    },
    [originalFeatureStatuses]
  );
  const handleTextChange = (e, name) => {
    const { value } = e.target;
    setHrefs((prevHrefs) => ({
      ...prevHrefs,
      [name]: value,
    }));
    setShowSave((prevShowSave) => ({
      ...prevShowSave,
      [name]: true,
    }));
  };
  const handleCheckboxChange = (e, name) => {
    const { checked } = e.target;
    if (e.target.name === "isNavBars")
      setIsNavBars((prevIsNavBars) => ({
        ...prevIsNavBars,
        [name]: checked,
      }));
    if (e.target.name === "isShowSectionTitles")
      setIsShowSectionTitles((prevIsShowSectionTitles) => ({
        ...prevIsShowSectionTitles,
        [name]: checked,
      }));
    setShowSave((prevShowSave) => ({
      ...prevShowSave,
      [name]: true,
    }));
  };
  const getAllFeatures = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        FeaturesController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        const updatedFeatures = FeaturesList.map((feature, index) => {
          const foundItem = data.docs.find(
            (item) => item.data().name === feature.name
          );
          if (foundItem) {
            return {
              ...feature,
              id: foundItem.id,
              status: foundItem.data().status,
              sequence: foundItem.data().sequence || index + 1,
              href: foundItem.data().href || "",
              isNavBar: foundItem.data().isNavBar || false,
              isShowSectionTitle: foundItem.data().isShowSectionTitle || false,
            };
          }
          return {
            ...feature,
            sequence: index + 1,
            href: "",
            isNavBar: false,
            isShowSectionTitle: false,
          };
        });
        const sortedFeatures = updatedFeatures.sort(
          (a, b) => a.sequence - b.sequence
        );
        const initialStatuses = {};
        const initialHrefs = {};
        const initialNavBars = {};
        const initialShowSectionTitles = {};
        sortedFeatures.forEach((feature) => {
          initialStatuses[feature.name] = feature.status;
          initialHrefs[feature.name] = feature.href;
          initialNavBars[feature.name] = feature.isNavBar;
          initialShowSectionTitles[feature.name] = feature.isShowSectionTitle;
        });
        setFeaturesList(sortedFeatures);
        setFeatureStatuses(initialStatuses);
        setOriginalFeatureStatuses(initialStatuses);
        setHrefs(initialHrefs);
        setIsNavBars(initialNavBars);
        setIsShowSectionTitles(initialShowSectionTitles);
        return "Features details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  const handleSubmit = ({ id = "", label, name }) => {
    saveFeatures({
      id: id,
      label: label,
      name: name,
      finallyFunc: () => {
        setLoading(false);
        setShowSave((prevShowSave) => ({
          ...prevShowSave,
          [name]: false,
        }));
        getAllFeatures();
      },
    });
  };
  const saveFeatures = ({ id = "", label, name, finallyFunc = () => {} }) => {
    toastPromise({
      promise: new Promise((resolve, reject) => {
        id
          ? FeaturesController.update({
              parameters: {
                id: id,
                label: label,
                name: name,
                status: featureStatuses[name],
                href: hrefs[name],
                isNavBar: isNavBars[name],
                isShowSectionTitle: isShowSectionTitles[name],
                sequence: featuresList.find((f) => f.name === name).sequence,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : FeaturesController.add({
              parameters: {
                label: label,
                name: name,
                status: featureStatuses[name],
                href: hrefs[name],
                isNavBar: isNavBars[name],
                isShowSectionTitle: isShowSectionTitles[name],
                sequence: featuresList.find((f) => f.name === name).sequence,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        return data;
      },
      errorMessage: (data) => data,
    }).finally(finallyFunc);
  };
  const saveSequence = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        const savePromises = featuresList.map((feature) =>
          FeaturesController.update({
            parameters: {
              id: feature.id,
              label: feature.label,
              name: feature.name,
              href: hrefs[feature.name],
              isNavBar: isNavBars[feature.name],
              isShowSectionTitle: isShowSectionTitles[feature.name],
              sequence: feature.sequence,
              lastModifiedBy: user.uid,
              lastModifiedDate: new Date().toLocaleString(),
            },
            resolve: resolve,
            reject: reject,
          })
        );
        Promise.all(savePromises).then(resolve).catch(reject);
      }),
      successMessage: (data) => {
        console.debug(data);
        return "Sequence saved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => {
      setLoading(false);
      setSequenceChanged(false);
    });
  };
  useEffect(() => {
    getAllFeatures();
  }, []);
  const handleDragStart = (index) => {
    setDraggedFeature(index);
  };
  const handleDragEnter = (index) => {
    const newList = [...featuresList];
    const draggedItem = newList[draggedFeature];
    newList.splice(draggedFeature, 1);
    newList.splice(index, 0, draggedItem);
    newList.forEach((item, idx) => {
      item.sequence = idx + 1;
    });
    setDraggedFeature(index);
    setFeaturesList(newList);
    setSequenceChanged(true);
  };
  const handleDragEnd = () => {
    setDraggedFeature(null);
  };

  return (
    <section id="featuresList">
      <h4>FEATURES LIST</h4>
      <hr />
      {sequenceChanged && (
        <Button onClick={saveSequence} disabled={loading}>
          {loading ? "Saving Sequence..." : "Save Sequence"}
        </Button>
      )}
      {featuresList.map((item, index) => (
        <div
          key={item.name}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragEnter={() => handleDragEnter(index)}
          onDragEnd={handleDragEnd}
          className="draggable-feature"
        >
          <div>
            <label className="text-label">{item.label}</label>
            <div className="wrapper">
              <div>
                <Textbox
                  type="text"
                  name="sequence"
                  value={item.sequence}
                  placeholder="Sequence"
                  readOnly={true}
                />
                <Dropdown
                  name={`${item.name}Status`}
                  title="Status"
                  value={featureStatuses[item.name] || item.status}
                  options={FeatureStatusList}
                  onChangeFunc={(value) => onHandleChange(value, item.name)}
                  isClearable={false}
                  placeholder="Status"
                />
                <Textbox
                  type="text"
                  name="href"
                  placeholder="href"
                  value={hrefs[item.name]}
                  onChange={(e) => handleTextChange(e, item.name)}
                />
                <Checkbox
                  name="isNavBars"
                  placeholder="isNavBars"
                  checked={isNavBars[item.name] || false}
                  onChange={(e) => handleCheckboxChange(e, item.name)}
                />
                <Checkbox
                  name="isShowSectionTitles"
                  placeholder="isShowSectionTitles"
                  checked={isShowSectionTitles[item.name] || false}
                  onChange={(e) => handleCheckboxChange(e, item.name)}
                />
              </div>
              {showSave[item.name] && (
                <Button
                  onClick={() =>
                    handleSubmit({
                      id: item.id,
                      label: item.label,
                      name: item.name,
                    })
                  }
                  disabled={loading}
                >
                  {loading ? "Saving" : "Save"}
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};
